import { useRef, useState } from "react";
import { Select } from "antd";
import { renderErrorNotifications } from "helpers/error.helpers";
import useDebouncedCallback from "hooks/useDebouncedCallback/useDebouncedCallback";
import { searchPlaces, getPlaceDetails } from "../../services/places.service";

const EMPTY_FIELDS = {
  name: "",
  address_line_1: "",
  address_line_2: "",
  city: "",
  state: "",
  postal_code: "",
  country_code: "IN",
  geo_lat: "",
  geo_lng: "",
};

/**
 * Venue search + auto-fill using BFF-proxied Google Places API.
 *
 * Props:
 *   onChange : (fields) => void — called with parsed address fields when a place is selected or cleared
 */
function VenueSearch({ onChange }) {
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [fetching, setFetching] = useState(false);
  const sessionToken = useRef(crypto.randomUUID());

  const handleSearch = useDebouncedCallback(async (input) => {
    if (!input || input.length < 2) {
      setOptions([]);
      return;
    }
    setSearching(true);
    const { data, errors } = await searchPlaces({
      input,
      sessionToken: sessionToken.current,
    });
    setSearching(false);
    if (errors?.length > 0) {
      renderErrorNotifications(errors);
      return;
    }
    setOptions(
      (data?.suggestions ?? []).map((s) => ({
        value: s.placeId,
        label: s.description,
      }))
    );
  });

  const handleSelect = async (placeId) => {
    setFetching(true);
    const { data, errors } = await getPlaceDetails({
      placeId,
      sessionToken: sessionToken.current,
    });
    setFetching(false);
    // Rotate session token after a details call (billing best practice)
    sessionToken.current = crypto.randomUUID();
    if (errors?.length > 0) {
      renderErrorNotifications(errors);
      return;
    }
    onChange?.(data);
  };

  const handleClear = () => {
    setOptions([]);
    onChange?.(EMPTY_FIELDS);
  };

  return (
    <div>
      <Select
        showSearch
        filterOption={false}
        onSearch={handleSearch}
        onSelect={handleSelect}
        onClear={handleClear}
        options={options}
        loading={searching || fetching}
        placeholder="Search venue name or address…"
        allowClear
        style={{ width: "100%" }}
        size="large"
        notFoundContent={searching ? "Searching…" : null}
      />
      <p style={{ fontSize: 12, color: "var(--showup-text-secondary, #888)", marginTop: 4 }}>
        Start typing a venue name (e.g. &ldquo;NESCO Center&rdquo;, &ldquo;JW Marriott Mumbai&rdquo;) &mdash; all fields will auto-fill.
      </p>
    </div>
  );
}

export default VenueSearch;
